// src/pages/commercial/CommercialFunnelPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';

export default function CommercialFunnelPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);

    // Standard Sales Stages
    const stages = ['lead', 'qualification', 'proposal', 'negotiation', 'won', 'lost'];
    const stageLabels = {
        'lead': 'Nouveaux',
        'qualification': 'Qualifiés',
        'proposal': 'Devis Envoyé',
        'negotiation': 'Négociation',
        'won': 'Gagnés',
        'lost': 'Perdus'
    };
    const stageColors = {
        'lead': '#94a3b8',
        'qualification': '#3b82f6',
        'proposal': '#8b5cf6',
        'negotiation': '#f59e0b',
        'won': '#10b981',
        'lost': '#ef4444'
    };

    useEffect(() => {
        async function fetchFunnel() {
            setLoading(true);
            try {
                const deals = await pb.collection('deals').getFullList({
                    filter: `owner = "${user.id}"`
                });

                // Count deals per stage
                const counts = stages.map(stage => {
                    const count = deals.filter(d => d.stage === stage || (stage === 'won' && d.status === 'won') || (stage === 'lost' && d.status === 'lost')).length;
                    // Note: Simplified logic. In reality, 'status' and 'stage' might be separate or same. Assuming existing code uses 'stage' or mapping 'status'.
                    // Let's assume deals have a 'stage' field OR we map status.
                    // If your deals only have 'status', adapt here. I'll search based on standard 'stage' field if it exists, or fallback.

                    // Specific fix based on typically used fields: 
                    // If deals use 'status' for everything:
                    const countByStatus = deals.filter(d => (d.status === stage) || (d.stage === stage)).length;

                    return {
                        id: stage,
                        label: stageLabels[stage],
                        count: countByStatus,
                        color: stageColors[stage],
                        value: deals.filter(d => (d.status === stage || d.stage === stage)).reduce((acc, d) => acc + (d.amount || 0), 0)
                    };
                });

                setStats(counts);
            } catch (err) {
                console.error("Error fetching funnel", err);
            } finally {
                setLoading(false);
            }
        }
        fetchFunnel();
    }, [user.id]);

    const maxCount = Math.max(...stats.map(s => s.count), 1);

    return (
        <CommercialLayout title="Entonnoir de Vente">
            <div className="commercial-page-wrapper">
                <style>{`
                .commercial-page-wrapper {
                    --admin-panel: var(--commercial-panel, #fff);
                    --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                    --admin-border: var(--commercial-border, #e2e8f0);
                    --admin-text: var(--commercial-text, #0f172a);
                    --admin-muted: var(--commercial-muted, #64748b);
                    --admin-accent: var(--commercial-accent, #4f46e5);
                }
                .funnel-container {
                    display: flex; flex-direction: column; gap: 12px;
                    max-width: 800px; margin: 0 auto;
                    padding: 40px 0;
                }
                .funnel-step {
                    display: flex; align-items: center; gap: 24px;
                }
                .funnel-label {
                    width: 150px; text-align: right; font-weight: 600; color: var(--admin-text);
                }
                .funnel-bar-container {
                    flex: 1; background: var(--admin-panel-2); border-radius: 8px; overflow: hidden; height: 48px;
                    border: 1px solid var(--admin-border);
                    position: relative;
                }
                .funnel-bar {
                    height: 100%; display: flex; align-items: center; padding: 0 16px;
                    color: white; font-weight: 700; text-shadow: 0 1px 2px rgba(0,0,0,0.1);
                    transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
                    white-space: nowrap;
                    position: relative;
                    /* Create funnel shape visual effect if wanted, but simple bars are cleaner for data */
                }
                .funnel-value {
                    margin-left: auto; font-size: 0.9rem; opacity: 0.9; font-weight: 500;
                }
                .funnel-meta {
                    width: 100px; text-align: left; font-size: 0.9rem; color: var(--admin-muted);
                }
                `}</style>

                <h1 style={{ textAlign: 'center', marginBottom: 40, fontSize: '1.8rem' }}>Analyse du Pipeline</h1>

                {loading ? (
                    <div style={{ textAlign: 'center', color: 'var(--admin-muted)' }}>Chargement des données...</div>
                ) : (
                    <div className="funnel-container">
                        {stats.map((step, index) => {
                            // Calculate width percentage relative to max
                            const width = (step.count / maxCount) * 100;
                            // Calculate conversion rate from previous step (simple approximation)
                            const prev = stats[index - 1];
                            const conversion = prev && prev.count > 0 ? Math.round((step.count / prev.count) * 100) : null;

                            return (
                                <div key={step.id} className="funnel-step">
                                    <div className="funnel-label">
                                        {step.label}
                                        {conversion !== null && (
                                            <div style={{ fontSize: '0.75rem', color: 'var(--admin-muted)', fontWeight: 400 }}>
                                                {conversion}% du niveau sup.
                                            </div>
                                        )}
                                    </div>
                                    <div className="funnel-bar-container">
                                        <div className="funnel-bar" style={{ width: `${Math.max(width, 0)}%`, minWidth: width > 0 ? '40px' : '0', background: step.color }}>
                                            {step.count > 0 && <span>{step.count}</span>}
                                        </div>
                                    </div>
                                    <div className="funnel-meta">
                                        {step.value > 0 ? (new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(step.value)) : '-'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div style={{ textAlign: 'center', marginTop: 40, color: 'var(--admin-muted)', fontStyle: 'italic', fontSize: '0.9rem' }}>
                    * Les taux de conversion sont indicatifs et basés sur le volume actuel de chaque étape.
                </div>
            </div>
        </CommercialLayout>
    );
}
